// src/lib/layer_transport.ts — cum ajunge un strat în browser: GeoJSON integral sau tile-uri
// vectoriale (MVT). PUR, partajat server↔client (anti-drift): serverul calculează `geom_bytes` doar
// pentru straturile pe care clientul le-ar cere ca GeoJSON, clientul decide cu ACEEAȘI regulă.
//
// Istoric: pragul era doar numărul de obiecte (4000 → 100 la F13, „pe PUG-uri reale costul e
// GEOMETRIA, nu numărul"). Dar sub 100 de obiecte încap și straturi de megaocteți — 25 de cursuri de
// apă × 160 KB = 4 MB, o limită de UAT de 940 KB într-un singur poligon — iar deschiderea unei hărți
// cu câteva astfel de straturi însemna 8 MB de GeoJSON (măsurat pe Oravița, 09.09.2026). Tile-urile
// le simplifică pe zoom și aduc doar ce e vizibil; clientul e deja pregătit pentru ambele moduri
// (înălțimi 3D prin heights3d, filtre prin `?filter=`, editarea ia geometria din API, nu din tile).

/** Peste atâtea obiecte, stratul se servește ca tile-uri — pe AMBELE hărți (regula istorică). */
export const MVT_THRESHOLD = 100

/**
 * Peste atâția octeți de geometrie PE DISC (suma `pg_column_size(geom)` a obiectelor curente),
 * stratul se servește ca tile-uri chiar dacă are puține obiecte. Calibrat 11.09.2026 pe baza de
 * dezvoltare: GeoJSON-ul e constant ~1,7× mărimea de pe disc, deci 300 KB ≈ 512 KB de GeoJSON;
 * din 209 straturi sub prag, 13 depășeau — exact cele grele. Se măsoară fără detoastare
 * (`pg_column_size` citește mărimea din pointerul TOAST), ~10 ms pe o hartă cu 51 de straturi mici.
 *
 * ⚠️ Criteriul ăsta se aplică DOAR pe harta PUBLICĂ. Tile-urile arată toate obiectele, dar geometria
 * e cuantizată pe zoom (`ST_AsMVTGeom`, extent 4096: ~4 cm la zoom 18, ~30 cm la 15) și TĂIATĂ la
 * marginea tile-ului, deci LIPIREA la desen poate prinde un vârf artificial. Cetățeanul nu editează
 * niciodată (`can_edit_map` întoarce false pe `CTX.is_public`), deci pentru el e câștig curat:
 * 16,2 MB → 2,5 MB la deschiderea portalului Armeniș. Funcționarul ar plăti precizie pentru o
 * viteză de care are mai puțină nevoie — el rămâne pe GeoJSON pentru straturile mici-dar-grele.
 * (Pe straturile de peste MVT_THRESHOLD obiecte se lipește de geometrie de tile ȘI azi — limitare
 * veche; reparația reală e lipirea din API, notată în TODO_PRODUCTIE.md.)
 */
export const MVT_GEOM_BYTES = 300_000

/**
 * URL-ul GeoJSON public al unui strat. ⚠️ `limit` se trimite DOAR când apelantul chiar vrea o pagină
 * (tabelul de atribute: 2000). Calea de DESENARE nu-l trimite — vrea stratul ÎNTREG, iar ruta publică
 * întoarce tot când `limit` lipsește. Contează mai ales cu `as_of`, unde tile-urile sunt dezactivate
 * (ruta de tile-uri n-are suport temporal) și deci ORICE strat, oricât de mare, vine pe aici: o
 * limită implicită ar desena tăcut un subset ales pe ordinea de import, fără legătură cu ce se vede.
 */
export function public_geojson_url(
	token: string,
	layer_slug: string,
	opts: { limit?: number; as_of?: string; attributes_only?: boolean } = {},
): string {
	const q = [
		`k=${encodeURIComponent(token)}`,
		opts.limit === undefined ? "" : `limit=${opts.limit}`,
		opts.attributes_only ? "attributes_only=1" : "",
		opts.as_of ? `as_of=${encodeURIComponent(opts.as_of)}` : "",
	].filter(Boolean)
	return `/api/public/layers/${encodeURIComponent(layer_slug)}/geojson?${q.join("&")}`
}

export interface TransportHint {
	feature_count: number
	// 0 = necalculat (strat peste MVT_THRESHOLD — e tile-uri oricum) sau fără geometrie.
	geom_bytes?: number
}

/**
 * Tile-uri vectoriale sau GeoJSON integral? Cu `as_of` rămâne GeoJSON — ruta de tile-uri n-are
 * suport temporal, iar snapshot-urile istorice sunt rare.
 *
 * `is_public` = harta cetățeanului (`CTX.is_public`). Numai acolo se aplică criteriul de GREUTATE;
 * criteriul pe NUMĂRUL de obiecte se aplică pe ambele, ca dintotdeauna.
 */
export function use_vector_tiles(layer: TransportHint, as_of = "", is_public = false): boolean {
	if (as_of) {
		return false
	}
	if ((layer.feature_count ?? 0) > MVT_THRESHOLD) {
		return true
	}
	return is_public && (layer.geom_bytes ?? 0) > MVT_GEOM_BYTES
}
